import React from 'react';

/**
 * UnifiedButton — the canonical action element.
 *
 * Variants:
 *  - primary   → solid brand fill (gold in dark / forest in light)
 *  - secondary → outlined neutral border
 *  - ghost     → text-only, hover bg
 *  - danger    → solid red fill
 *  - success   → solid green fill
 *  - link      → text + underline on hover
 *
 * Sizes: sm | md | lg
 * Block: stretches to 100% width when true
 */
export const UnifiedButton = React.forwardRef(function UnifiedButton(
  {
    variant = 'primary',
    size = 'md',
    block = false,
    loading = false,
    disabled = false,
    leftIcon = null,
    rightIcon = null,
    className = '',
    style,
    children,
    testId,
    type = 'button',
    ...rest
  },
  ref
) {
  const sizeStyles = {
    sm: { padding: '0.375rem 0.75rem', fontSize: 'var(--mesp-font-sm)', minHeight: 32 },
    md: { padding: '0.5rem 1rem',    fontSize: 'var(--mesp-font-body)', minHeight: 40 },
    lg: { padding: '0.75rem 1.5rem', fontSize: 'var(--mesp-font-h4)', minHeight: 48 },
  }[size];

  const variantMap = {
    primary: {
      background: 'var(--mesp-primary)',
      color: 'var(--mesp-primary-fg)',
      border: '1px solid var(--mesp-primary)',
      boxShadow: 'var(--mesp-shadow-button)',
    },
    secondary: {
      background: 'transparent',
      color: 'var(--mesp-text)',
      border: '1px solid var(--mesp-border)',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--mesp-text-muted)',
      border: '1px solid transparent',
    },
    danger: {
      background: 'var(--mesp-danger)',
      color: '#FFFFFF',
      border: '1px solid var(--mesp-danger)',
    },
    success: {
      background: 'var(--mesp-success)',
      color: '#FFFFFF',
      border: '1px solid var(--mesp-success)',
    },
    link: {
      background: 'transparent',
      color: 'var(--mesp-accent)',
      border: '1px solid transparent',
      padding: '0.25rem 0.5rem',
      textDecoration: 'none',
    },
  };

  const isDisabled = disabled || loading;

  const handleEnter = (e) => {
    if (isDisabled) return;
    if (variant === 'primary')   e.currentTarget.style.filter = 'brightness(1.08)';
    if (variant === 'secondary' || variant === 'ghost') {
      e.currentTarget.style.background = 'var(--mesp-gold-soft)';
      e.currentTarget.style.borderColor = 'var(--mesp-border-strong)';
      e.currentTarget.style.color = 'var(--mesp-text)';
    }
    if (variant === 'danger')    e.currentTarget.style.filter = 'brightness(1.10)';
    if (variant === 'success')   e.currentTarget.style.filter = 'brightness(1.10)';
    if (variant === 'link')      e.currentTarget.style.textDecoration = 'underline';
    e.currentTarget.style.transform = 'translateY(-1px)';
  };
  const handleLeave = (e) => {
    if (isDisabled) return;
    e.currentTarget.style.filter = '';
    if (variant === 'secondary' || variant === 'ghost') {
      e.currentTarget.style.background = 'transparent';
      e.currentTarget.style.borderColor = variant === 'secondary' ? 'var(--mesp-border)' : 'transparent';
      e.currentTarget.style.color = variantMap[variant].color;
    }
    if (variant === 'link') e.currentTarget.style.textDecoration = 'none';
    e.currentTarget.style.transform = 'translateY(0)';
  };

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      data-testid={testId}
      className={`mesp-ui mesp-focus-ring ${className}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      style={{
        ...variantMap[variant],
        ...sizeStyles,
        width: block ? '100%' : 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        borderRadius: 'var(--mesp-radius-md)',
        fontWeight: 'var(--mesp-font-weight-semibold)',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.5 : 1,
        transition: 'transform var(--mesp-trans-fast), filter var(--mesp-trans-fast), background var(--mesp-trans-fast), border-color var(--mesp-trans-fast)',
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...rest}
    >
      {loading ? (
        <span
          aria-hidden
          style={{
            width: 14, height: 14,
            border: '2px solid currentColor',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            display: 'inline-block',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      ) : leftIcon}
      <span>{children}</span>
      {!loading && rightIcon}
    </button>
  );
});

export default UnifiedButton;
